import { toast } from '@wellesley/system-toast'
import { ENTER_KEY } from 'constants/key-codes'
import { useSelector } from 'react-redux'
import { useIsMobile } from '@wellesley/ui'
import { useSwitchState } from 'hooks/useSwitchState'
import getTextVisibleLength from 'helpers/getTextVisibleLength'
import { platformSettingsSlice } from '@wellesley/settings'
import { useEffect, useState, KeyboardEvent } from 'react'
import { useUploadMedia, UseUploadMediaProps } from '@wellesley/uploads'
import { PollCreate, useGetApiV1PostsByPostIdQuery } from '@wellesley/openapi'

import comparePoll from '../../helpers/comparePoll'
import { TPostForm } from '../../types'
import compareUploads from '../../helpers/compareUploads'
import useSensitiveControl from '../../ControlsBar/SensitiveControl/hooks/useSensitiveControl'
import useLocationControl from '../../ControlsBar/LocationSelector/hooks/useLocationControl'
import getDefaultPrivacy from '../../helpers/privacy/getDefaultPrivacy'

import useFocusTextarea from '../useFocusTextarea'
import { useTextHandlers } from '../useTextHandlers'
import { usePrivacyHandlers } from '../usePrivacyHandlers'

const DEFAULT_VALUES = {
  text: '',
  contentWarning: '',
  isSensitive: false,
  isPreviewLink: true,
  isAllowedComments: true,
}

type TUseSubmitPost = Omit<TPostForm, 'privacyOptions'> &
  Pick<UseUploadMediaProps, 'closeCb'>

const useSubmitPost = ({
  feed,
  isReply,
  isModal,
  autoFocus = false,
  isOpenModal = false,
  fillInit = true,
  closeCb,
  onSubmit,
  onMediaChange,
  authorIdentityId,
  ownerId,
}: TUseSubmitPost) => {
  const isEdit = !!feed && (!!feed.uploads?.length || !!feed.text)
  const isEditComment = feed?.id !== feed?.conversationId

  const [isPreviewLink, showPreviewLink, onHidePreviewLink] =
    useSwitchState(true)
  const { text, setText, handleTextareaChange } = useTextHandlers()
  const defaultPrivacy = getDefaultPrivacy(isReply || isEditComment)

  const { privacy, resetPrivacy, ...privacyProps } = usePrivacyHandlers(
    isReply || isEditComment,
    defaultPrivacy,
  )

  const [isAllowedComments, , , onAllowComments] = useSwitchState(
    isEdit ? feed?.settings.commentsEnabled : DEFAULT_VALUES.isAllowedComments,
  )

  const [pollData, setPollData] = useState<PollCreate | null>(null)
  const [isPollStep, , , togglePollStep, setIsPollStep] = useSwitchState(
    !!feed?.poll,
  )

  // Reset poll step when modal is closed for new post
  useEffect(() => {
    if (isEdit) return

    if (!isPollStep) setIsPollStep(false)
  }, [isOpenModal])

  const postSizeLimit = useSelector(
    platformSettingsSlice.selectors.selectPostConfig,
  ).max_characters

  const isMobile = useIsMobile()

  const { resetUploads, uploads, ...mediaProps } = useUploadMedia({
    feed: fillInit ? feed : feed ? { ...feed, uploads: [] } : undefined,
    closeCb,
    resetOnClose: true,
    previewSize: isMobile ? 150 : 250,
    onMediaChange,
  })

  const { isSensitive, toggleSensitive, contentWarning, setContentWarning } =
    useSensitiveControl(feed)

  const { location, setLocation, clearLocation } = useLocationControl(feed)

  const { data: feedData, isLoading: isFeedLoading } =
    useGetApiV1PostsByPostIdQuery(
      {
        postId: feed?.id || '',
        htmlContent: false,
        withComments: false,
        includePublisher: false,
        withRepostedBy: false,
        withTrends: false,
      },
      { skip: !isModal || !feed || !feed.id },
    )

  useEffect(() => {
    feed && isEdit && !isReply && privacyProps.setPrivacy(feed.privacy)
  }, [])

  const [textFieldRef, focusTextArea] = useFocusTextarea(autoFocus)

  useEffect(() => {
    if (fillInit && feedData) {
      setText(feedData.text)
      focusTextArea()
    }
  }, [feedData])

  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  const clearForm = () => {
    setText(DEFAULT_VALUES.text)
    resetUploads()
    resetPrivacy()
    setIsSubmitLoading(false)
  }

  const handleSubmitPost = async () => {
    if (!text.trim() && !uploads.length) {
      return
    }

    setIsSubmitLoading(true)

    try {
      const uploadIds = await Promise.all(uploads.map((u) => u.upload))

      const isSensitiveContentEnabled = !!contentWarning

      await onSubmit({
        text: text.trim(),
        privacy,
        kind: 'Note',
        commentsEnabled: isAllowedComments,
        attachments: uploadIds,
        sensitive: isSensitiveContentEnabled,
        withPreview: isPreviewLink,
        ...(isPollStep &&
          pollData && {
            poll: {
              ...pollData,
              // TODO: BE support null but openapi doesn't show proper type for expiresIn
              expiresIn: pollData?.expiresIn || (null as unknown as number),
            },
          }),
        ...(isSensitiveContentEnabled && { contentWarning }),
        ...(ownerId && {
          ownerId,
          ...(ownerId === authorIdentityId && { postAsOwner: true }),
        }),
        ...(location && { address: location }),
        ...(isEdit && feed?.address && !location && { removeAddress: true }),
      })

      clearForm()
      setContentWarning(DEFAULT_VALUES.contentWarning)
      toggleSensitive()
      showPreviewLink()

      return
    } catch (e) {
      toast.error(`Can not create post: ${e as string}`)
      setIsSubmitLoading(false)
    }
  }

  const handleEnterKeyDown = (evt: KeyboardEvent<HTMLTextAreaElement>) => {
    const { altKey, ctrlKey, key, metaKey } = evt
    const canSubmit = !altKey && (ctrlKey || metaKey) && key === ENTER_KEY

    canSubmit && void handleSubmitPost()
  }

  const isTextChanged = text.trim() !== (feedData?.text ?? DEFAULT_VALUES.text)
  const isValidText = getTextVisibleLength(text) <= postSizeLimit

  const isUploadsChanged = compareUploads(uploads, feed)
  const isPollChanged = isTextChanged || comparePoll(pollData, feed)

  const hasUploads = uploads.length > 0

  const canUpdatePost =
    feedData?.text !== text ||
    feedData?.privacy !== privacy ||
    feedData?.settings.commentsEnabled !== isAllowedComments ||
    (hasUploads &&
      (feedData?.summary ?? DEFAULT_VALUES.contentWarning) !==
        contentWarning) ||
    feedData?.settings.sensitive !== isSensitive ||
    isUploadsChanged ||
    isPollChanged ||
    !isPreviewLink ||
    location?.formatted !== feed?.address?.formatted

  const canCreatePost =
    text.trim().length > 0 || uploads.filter(Boolean).length > 0

  const isValidPoll =
    !!pollData &&
    pollData?.options.length >= 2 &&
    new Set(pollData.options.filter(Boolean)).size === pollData.options.length

  const canSubmitPost =
    isValidText &&
    (isEdit ? canUpdatePost && canCreatePost : canCreatePost) &&
    (pollData ? isValidPoll : true)

  const isPostChanged =
    isTextChanged ||
    contentWarning !== (feedData?.summary ?? DEFAULT_VALUES.contentWarning) ||
    isSensitive !==
      (feedData?.settings.sensitive ?? DEFAULT_VALUES.isSensitive) ||
    isPreviewLink !== DEFAULT_VALUES.isPreviewLink ||
    isAllowedComments !==
      (feedData?.settings.commentsEnabled ??
        DEFAULT_VALUES.isAllowedComments) ||
    isUploadsChanged ||
    location?.formatted !== feed?.address?.formatted

  const isChangedValues = isPollStep ? isPollChanged : isPostChanged

  return {
    text,
    privacy,
    setText,
    isEdit,
    uploads,
    hasUploads,
    isFeedLoading,
    isSubmitLoading,
    textFieldRef,
    isSensitive,
    isPreviewLink,
    isChangedValues,
    contentWarning,
    canSubmitPost,
    isAllowedComments,
    isValidText,
    isPollStep,
    togglePollStep,
    setPollData,
    toggleSensitive,
    setContentWarning,
    location,
    setLocation,
    clearLocation,
    onAllowComments,
    handleSubmitPost,
    onHidePreviewLink,
    handleEnterKeyDown,
    handleTextareaChange,
    ...mediaProps,
    ...privacyProps,
  }
}

export { useSubmitPost }
